/**
 * Location_Service.ts
 * Location operations  (sk-catalog-api /api/v1/locations)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Locations_Url,
  Catalog_Location_Url,
  Platform_Locations_Url,
} from '../../api/endpoints';
import {
  Location,
  CreateLocationPayload,
  LocationsResponse,
  PlatformLocation,
} from '../../schemas/catalog/location';

/* ─────────────────────────────────────────
   Platform branches — GET /v1/locations
   Platform owns the branch; catalog only mirrors one. This is where a
   location id comes from, so a catalog location can be joined to platform
   and to sk-crm instead of being an id only catalog understands.
───────────────────────────────────────── */

export const getPlatformLocations = async (): Promise<PlatformLocation[]> => {
  const { data } = await axiosInstance.get<{ data: PlatformLocation[] }>(Platform_Locations_Url);
  return data?.data ?? [];
};

/* ─────────────────────────────────────────
   List — GET /api/v1/locations
───────────────────────────────────────── */

export const getLocations = async (): Promise<LocationsResponse> => {
  const { data } = await axiosInstance.get<LocationsResponse>(Catalog_Locations_Url);
  return data;
};

/* ─────────────────────────────────────────
   Create — POST /api/v1/locations  { name }
───────────────────────────────────────── */

export const createLocation = async (payload: CreateLocationPayload): Promise<Location> => {
  const { data } = await axiosInstance.post<Location>(Catalog_Locations_Url, payload);
  return data;
};

/* ─────────────────────────────────────────
   Get one — GET /api/v1/locations/:id
───────────────────────────────────────── */

export const getLocation = async (id: string): Promise<Location> => {
  const { data } = await axiosInstance.get<Location>(Catalog_Location_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Update — PATCH /api/v1/locations/:id  { name }
───────────────────────────────────────── */

export const updateLocation = async (id: string, payload: Partial<CreateLocationPayload>): Promise<Location> => {
  const { data } = await axiosInstance.patch<Location>(Catalog_Location_Url(id), payload);
  return data;
};

/* ─────────────────────────────────────────
   Delete — DELETE /api/v1/locations/:id
   Refused (409 LOCATION_IN_USE) when location-scoped field values reference it.
───────────────────────────────────────── */

export const deleteLocation = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Location_Url(id));
};
